import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  FileText, Download, Search, Calendar, DollarSign,
  Clock, Eye, CheckCircle, AlertCircleIcon
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import SharedFooter3 from "../Footer/SharedFooter3";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { 
  fetchInvoices, 
  clearInvoicesError,
  setInvoicesSearchQuery,
  clearAllInvoices
} from "../redux/Slices/InvoiceSlice";
import { Invoice, InvoiceStatusEnum } from "../modules/shared/model";
import { 
  downloadInvoicePdf, 
  getInvoiceById,
  getZohoInvoiceUrl,
  getUniqueInvoiceNames
} from "../redux/Actions/InvoiceActions";
import ZohoInvoiceViewer from "../components/invoices/ZohoInvoiceViewer";
import SharedHeader5 from "../Headers/SharedHeader5";
import { useTranslation } from "react-i18next";

// Helper function to check if invoice should be displayed as negative
const isCreditNote = (invoice: any): boolean => {
  // Check if it's a credit note from Zoho metadata
  if (invoice.zohoMetadata?.type === "credit_note" || invoice.invoiceType === "credit_note") {
    return true;
  }
  
  // Check if invoice number includes "CN" (Credit Note)
  if (invoice.invoiceNumber && invoice.invoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  // Check if Zoho invoice number includes "CN"
  if (invoice.zohoInvoiceNumber && invoice.zohoInvoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  return false;
};

// Helper function to format invoice amount
const formatInvoiceAmount = (invoice: any): string => {
  const amount = invoice.amount || 0;
  const displayAmount = isCreditNote(invoice) ? -amount : amount;
  
  return `${getDynamicCurrencySymbol()}${displayAmount.toFixed(2)}`;
};

const ClientInvoice = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state
  const {
    invoices,
    invoicesLoading,
    invoicesError,
    invoicesTotalCount,
    invoicesCurrentPage,
    invoicesHasNextPage,
    invoicesHasPreviousPage,
    invoicesSearchQuery
  } = useSelector((state: any) => state.invoices);

  // Get current user
  const { user } = useSelector((state: any) => state.auth);

  // Local state management
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedInvoiceName, setSelectedInvoiceName] = useState("all");
  const [invoiceNames, setInvoiceNames] = useState<string[]>([]);
  const [showZohoViewer, setShowZohoViewer] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch unique invoice names on component mount
  useEffect(() => {
    const fetchInvoiceNames = async () => {
      try {
        const names = await getUniqueInvoiceNames();
        setInvoiceNames(names);
      } catch (error) {
        console.error("Error fetching invoice names:", error);
      }
    };
    fetchInvoiceNames();
  }, []);

  // Fetch invoices on component mount and when filters change
  useEffect(() => {
    if (!user?.id) return;

    const queryParams: Record<string, string | number | boolean> = {
      page: currentPage,
      limit: 10,
      senderId: user.id, // Filter by client (sender for client invoices)
      receiverId: user.id, // Filter by client (receiver for platform invoices)
    };

    // Add search query if exists
    if (invoicesSearchQuery) {
      queryParams.search = invoicesSearchQuery;
    }

    // Add filters
    if (selectedStatus !== "all") {
      queryParams.status = selectedStatus;
    }

    if (selectedInvoiceName !== "all") {
      queryParams.invoiceName = selectedInvoiceName;
    }

    // Add sorting
    if (sortBy) {
      queryParams.sort = sortBy;
    }

    dispatch(fetchInvoices(queryParams));
  }, [dispatch, currentPage, invoicesSearchQuery, selectedStatus, selectedInvoiceName, sortBy, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearAllInvoices());
      dispatch(clearInvoicesError());
    };
  }, [dispatch]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    dispatch(setInvoicesSearchQuery(value));
  };

  // Handle PDF download from Zoho
  const handleDownloadPDF = async (invoice: Invoice) => {
    // Show loader
    const loadingToast = document.createElement("div");
    loadingToast.className = "fixed top-4 right-4 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    loadingToast.innerHTML = `<div class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> ${t("clientInvoicePage.downloadingPDF")}`;
    document.body.appendChild(loadingToast);

    try {
      // Check for zohoInvoiceId (primary check)
      if (invoice.zohoInvoiceId) {
        const blob = await downloadInvoicePdf(invoice.id);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Show success message
        loadingToast.className = "fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
        loadingToast.innerHTML = t("clientInvoicePage.pdfDownloadedSuccess");
        setTimeout(() => loadingToast.remove(), 2000);
      } else {
        // Try to fetch fresh invoice data in case the field was missing
        try {
          const freshInvoice = await getInvoiceById(invoice.id);
          
          if (freshInvoice.zohoInvoiceId) {
            const blob = await downloadInvoicePdf(invoice.id);
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `invoice-${invoice.invoiceNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            // Show success message
            loadingToast.className = "fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
            loadingToast.innerHTML = t("clientInvoicePage.pdfDownloadedSuccess");
            setTimeout(() => loadingToast.remove(), 2000);
            return;
          }
        } catch (fetchError) {
          console.error("Error fetching fresh invoice data:", fetchError);
        }
        
        // If still no Zoho invoice ID, show message
        loadingToast.remove();
        alert(t("clientInvoicePage.invoiceNotInZoho"));
      }
    } catch (error) {
      console.error("Error downloading invoice PDF from Zoho:", error);
      loadingToast.className = "fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
      loadingToast.innerHTML = t("clientInvoicePage.failedToDownloadPDF");
      setTimeout(() => loadingToast.remove(), 3000);
      alert(t("clientInvoicePage.failedToDownloadZoho"));
    }
  };

  // Handle opening invoice in Zoho viewer
  const handleViewInZoho = async (invoice: Invoice) => {
    try {
      if (invoice.zohoInvoiceId) {
        setShowZohoViewer(invoice);
      } else {
        alert(t("clientInvoicePage.invoiceNotAvailable"));
      }
    } catch (error) {
      console.error("Error opening Zoho invoice:", error);
      alert(t("clientInvoicePage.invoiceNotAvailable"));
    }
  };

  // Calculate total metrics
  const totalAmount = invoices?.reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;
  const totalPaidAmount = invoices?.filter(i => i.status?.name === "Paid" || i.status?.id === InvoiceStatusEnum.Paid)
    .reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;
  const totalPendingAmount = invoices?.filter(i => i.status?.name === "Unpaid" || i.status?.id === InvoiceStatusEnum.Unpaid)
    .reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <SharedHeader5 />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t("clientInvoicePage.title")}</h1>
                <p className="text-gray-600 mt-1">{t("clientInvoicePage.subtitle")}</p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">{t("clientInvoicePage.totalAmount")}</p>
                    <p className="text-2xl font-semibold">{getDynamicCurrencySymbol()}{totalAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <span className="text-xl font-bold text-blue-600">{getDynamicCurrencySymbol()}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">{t("clientInvoicePage.paid")}</p>
                    <p className="text-2xl font-semibold">{getDynamicCurrencySymbol()}{totalPaidAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">{t("clientInvoicePage.pending")}</p>
                    <p className="text-2xl font-semibold">{getDynamicCurrencySymbol()}{totalPendingAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("clientInvoicePage.searchPlaceholder")}
                    value={invoicesSearchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>

                <select
                  value={selectedInvoiceName}
                  onChange={(e) => {
                    setSelectedInvoiceName(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="all">{t("clientInvoicePage.allInvoiceTypes")}</option>
                  {invoiceNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="all">{t("clientInvoicePage.allStatus")}</option>
                    <option value="paid">{t("clientInvoicePage.paid")}</option>
                    <option value="unpaid">{t("clientInvoicePage.unpaid")}</option>
                    <option value="overdue">{t("clientInvoicePage.overdue")}</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="newest">{t("clientInvoicePage.newestFirst")}</option>
                    <option value="oldest">{t("clientInvoicePage.oldestFirst")}</option>
                    <option value="amount_high">{t("clientInvoicePage.highestAmount")}</option>
                    <option value="amount_low">{t("clientInvoicePage.lowestAmount")}</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Invoices Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.invoice")}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.professional")}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.amount")}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.status")}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.date")}
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("clientInvoicePage.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoicesLoading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2">{t("clientInvoicePage.loadingInvoices")}</span>
                          </div>
                        </td>
                      </tr>
                    ) : invoices?.length > 0 ? (
                      invoices.map((invoice: Invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FileText className="h-5 w-5 text-gray-400 mr-2" />
                              <div className="font-medium text-gray-900">{invoice.invoiceNumber}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium">
                                {invoice.receiver ? 
                                  `${invoice.receiver.firstName || ""} ${invoice.receiver.lastName || ""}`.trim() || "N/A" :
                                  t("clientInvoicePage.platform")
                                }
                              </div>
                              <div className="text-sm text-gray-500">
                                {invoice.receiver?.email || (invoice.receiver ? "" : "platform@kokobeo.com")}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${isCreditNote(invoice) ? "text-red-600" : "text-gray-900"}`}>
                              {formatInvoiceAmount(invoice)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={
                              invoice.status?.name === "Paid" || invoice.status?.id === InvoiceStatusEnum.Paid ? "bg-green-100 text-green-700" :
                              invoice.status?.name === "Unpaid" || invoice.status?.id === InvoiceStatusEnum.Unpaid ? "bg-yellow-100 text-yellow-700" :
                              "bg-red-100 text-red-700"
                            }>
                              {invoice.status?.name?.toUpperCase() || "UNKNOWN"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-2 justify-end">
                              {(invoice as any).zohoInvoiceId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewInZoho(invoice)}
                                  className="text-green-600 hover:text-green-900"
                                  title={t("clientInvoicePage.viewPDF")}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPDF(invoice)}
                                className="text-gray-600 hover:text-gray-900"
                                title={t("clientInvoicePage.downloadPDF")}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                          {t("clientInvoicePage.noInvoicesFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    {t("clientInvoicePage.showingPage", { 
                      current: invoicesCurrentPage, 
                      total: Math.ceil((invoicesTotalCount || 0) / 10) 
                    })}
                  </span>
                  <span className="text-sm text-gray-500">
                    {t("clientInvoicePage.totalInvoices", { count: invoicesTotalCount || 0 })}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={!invoicesHasPreviousPage || invoicesLoading}
                  >
                    {t("clientInvoicePage.previous")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!invoicesHasNextPage || invoicesLoading}
                  >
                    {t("clientInvoicePage.next")}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Modals */}
      <ZohoInvoiceViewer
        invoice={showZohoViewer}
        isOpen={!!showZohoViewer}
        onClose={() => setShowZohoViewer(null)}
      />

      {/* Footer */}
      <footer className="bg-white border-t w-full mt-auto">
        <div className="max-w-7xl mx-auto">
          <SharedFooter3 />
        </div>
      </footer>
    </div>
  );
};

export default ClientInvoice;

